import React from "react";
import { useTranslation } from "react-i18next";
import { cn } from "@/lib/utils";
import { formatWithUnit, UnitType, UnitSystem } from "@/lib/units";

interface MeasurementValueProps {
  value: number;
  unit: string;
  type: UnitType;
  decimals?: number;
  showUnitName?: boolean;
  className?: string;
  forceSystem?: UnitSystem;
  forceUnit?: string;
  tooltipPlacement?: "top" | "bottom" | "left" | "right";
}

/**
 * MeasurementValue - A component for displaying measurement values with proper localization
 * 
 * This component automatically converts between metric/imperial units based on locale
 * and provides translated unit names when needed
 */
export function MeasurementValue({
  value,
  unit,
  type,
  decimals = 2,
  showUnitName = false,
  className,
  forceSystem,
  forceUnit,
  tooltipPlacement = "top"
}: MeasurementValueProps) {
  const { t, i18n } = useTranslation();
  
  // Format the value with appropriate unit
  const formattedValue = formatWithUnit(value, unit, type, {
    decimals,
    system: forceSystem,
    forceUnit
  });
  
  // Extract unit symbol from formatted value
  const parts = formattedValue.split(" ");
  const numericPart = parts.slice(0, -1).join(" ");
  const unitSymbol = parts[parts.length - 1];
  
  // If we need to show the unit name, translate it with proper pluralization
  const unitName = showUnitName 
    ? t(`units.${type}.${unitSymbol}${Math.abs(value) !== 1 ? '_plural' : ''}`) 
    : null;

  // Create display string
  let displayString = formattedValue;
  if (showUnitName && unitName) {
    displayString = `${numericPart} ${unitName}`;
  }

  return (
    <span 
      className={cn("whitespace-nowrap", className)} 
      title={showUnitName ? undefined : displayString}
      data-tooltip-placement={tooltipPlacement}
    >
      {displayString}
    </span>
  );
}

/**
 * FlowValue - Specialized component for flow rate measurements
 */
export function FlowValue(props: Omit<MeasurementValueProps, "type">) {
  return <MeasurementValue {...props} type="flow" />;
}

/**
 * PowerValue - Specialized component for power measurements
 */
export function PowerValue(props: Omit<MeasurementValueProps, "type">) {
  return <MeasurementValue {...props} type="power" />;
}

/**
 * EnergyValue - Specialized component for thermal energy measurements
 */
export function EnergyValue(props: Omit<MeasurementValueProps, "type">) {
  return <MeasurementValue {...props} type="energy" />;
}

/**
 * ElectricalEnergyValue - Specialized component for electrical energy measurements (always kWh)
 */
export function ElectricalEnergyValue(props: Omit<MeasurementValueProps, "type">) {
  return <MeasurementValue {...props} type="electricalEnergy" />;
}

/**
 * TemperatureValue - Specialized component for temperature measurements
 */
export function TemperatureValue(props: Omit<MeasurementValueProps, "type">) {
  return <MeasurementValue {...props} type="temperature" />;
}

/**
 * PressureValue - Specialized component for pressure measurements
 */
export function PressureValue(props: Omit<MeasurementValueProps, "type">) {
  return <MeasurementValue {...props} type="pressure" />;
}

/**
 * HeadValue - Specialized component for pump head measurements
 */
export function HeadValue(props: Omit<MeasurementValueProps, "type">) {
  return <MeasurementValue {...props} type="head" />;
}